// Sugalarm — cycle 328: the gentle "add a photo" nudge's suppression seam.
//
// Two levels, deliberately different in strength (mirrors the cycle-316
// alarmTour store's device-local-persistence convention). Nothing here
// syncs through JSONB settings or `settings_update` frames — this is
// per-device UI state, keyed per member.
//
//   • PERSISTENT opt-out (localStorage) — set ONLY when the user taps
//     "Don't remind me again." Once set, the nudge never shows again for
//     that member on that device, across future launches.
//
//   • SOFT session marker (sessionStorage) — set on "Not now", on
//     tap-away, and once the nudge has been shown, so it appears at most
//     once per app session. NOT persisted across launches: a fresh
//     session (a real app relaunch / kiosk reboot clears sessionStorage)
//     re-evaluates, so the nudge gently returns until the member either
//     adds a photo (avatar presence is the "resolved" state — no flag)
//     or opts out. sessionStorage (not plain in-memory) is chosen so the
//     "once per session" guarantee survives the cycle-33 poller's
//     same-tab `location.replace` reloads, which are NOT a new session.
//
// There is deliberately NO "done on upload/skip" flag: avatar presence
// itself is the resolved state (once `avatar_id` is non-null the nudge is
// inert), and a soft dismiss must NOT be persisted — that's the whole
// point of "gentle + recurring."

const OPT_OUT_PREFIX = "sugalarm.avatarNudgeOptOut.";
const SESSION_PREFIX = "sugalarm.avatarNudgeShown.";

// In-memory fallback for the soft marker when sessionStorage is
// unavailable (private-mode edge cases) — still session-scoped by process
// lifetime, never persisted.
const shownFallback = new Set<string>();

/** Persistent opt-out: true once the user chose "Don't remind me again"
 *  for this member on this device. */
export function isAvatarNudgeOptedOut(memberId: string): boolean {
  try {
    return localStorage.getItem(OPT_OUT_PREFIX + memberId) === "1";
  } catch {
    // No storage → treat as opted out so a broken store can never loop
    // the nudge (fail closed, matching the alarmTour convention).
    return true;
  }
}

/** Set the persistent opt-out — the nudge never shows again for this
 *  member on this device. Called ONLY from "Don't remind me again." */
export function setAvatarNudgeOptOut(memberId: string): void {
  try {
    localStorage.setItem(OPT_OUT_PREFIX + memberId, "1");
  } catch {
    /* storage unavailable — the in-memory close still ends this showing */
  }
}

/** Soft session marker: true once the nudge has been shown or soft-
 *  dismissed this session for this member (suppresses it for the rest of
 *  the session; a fresh session re-evaluates). */
export function isAvatarNudgeShownThisSession(memberId: string): boolean {
  try {
    if (sessionStorage.getItem(SESSION_PREFIX + memberId) === "1") return true;
  } catch {
    /* fall through to the in-memory set */
  }
  return shownFallback.has(memberId);
}

/** Mark the soft session marker — set when the nudge is shown, on "Not
 *  now", and on tap-away. Not persisted across launches. */
export function markAvatarNudgeShownThisSession(memberId: string): void {
  shownFallback.add(memberId);
  try {
    sessionStorage.setItem(SESSION_PREFIX + memberId, "1");
  } catch {
    /* sessionStorage unavailable — the in-memory set covers this session */
  }
}
